import { NavLink } from 'react-router-dom'
import { LineIcon } from './LineIcon'
import type { ConnectorHealth, LandscapeSystem } from '../types/landscape'

// The Connect menu as a checklist: every connection point Scout has, and whether it is still working.
// "If one of the connectors has become disconnected after being connected for any reason, it should
// show a warning symbol instead of a green checkmark" (owner, 2026-09-13). The API decides the health
// and writes the reason; this component only draws it, so the menu and the Landscape page can never
// disagree about a connector.

/** The glyph each state wears. Line icons from the one set; the colour is the row's class. */
export const HEALTH_GLYPH: Record<ConnectorHealth, string> = {
  ok: 'check',
  warning: 'warning',
  off: 'circle',
  coming: 'dash',
}

/** What the glyph means, for anyone reading it with their ears rather than their eyes. */
export const HEALTH_LABEL: Record<ConnectorHealth, string> = {
  ok: 'connected',
  warning: 'needs reconnecting',
  off: 'not connected',
  coming: 'coming',
}

/** The word that trails the name. Only where there is something to say: a working connector and one
 * nobody has connected yet both speak for themselves. */
const TRAILING: Partial<Record<ConnectorHealth, string>> = { warning: 'reconnect', coming: 'coming' }

export function ConnectGlyph({ health, size = 14 }: { health: ConnectorHealth; size?: number }) {
  return <LineIcon name={HEALTH_GLYPH[health]} size={size} className={`connect-glyph ${health}`} title={HEALTH_LABEL[health]} />
}

export function ConnectChecklist({ systems }: { systems: LandscapeSystem[] }) {
  return (
    <ul className="connect-checklist">
      {systems.map((s) => {
        // A system still on the map leads nowhere: there is no screen to open and nothing to connect,
        // so it is a dimmed line, not a link that disappoints.
        const linked = s.health !== 'coming' && s.route !== null
        const body = (
          <>
            <ConnectGlyph health={s.health} />
            <span className="connect-name">{s.name}</span>
            {TRAILING[s.health] && <span className="connect-note">{TRAILING[s.health]}</span>}
          </>
        )
        return (
          <li key={s.id} className={`connect-row ${s.health}`} title={s.healthReason ?? undefined}>
            {linked ? (
              <NavLink to={s.route!} className={({ isActive }) => (isActive ? 'nav-item connect-link active' : 'nav-item connect-link')}>
                {body}
              </NavLink>
            ) : (
              <span className="nav-item connect-link unlinked">{body}</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}
